/**
 * Dashboard materiaal section (Fase 6b).
 *
 * Full parity with the MateriaalTab fase view for ONE fase: view + CSV upload +
 * CAD import + delete + opgelijst toggle. Reuses the shared Fase 6a pieces:
 *   - `buildFaseGroups` to assemble pickings/lines/moves per task,
 *   - `MateriaalFaseGroup` for the per-fase composite (header, upload, tables),
 *   - `useMateriaalFaseControls` + `MateriaalCadModals` for the orchestration glue.
 *
 * The dashboard groups by *fase* (parent task). A legacy materiaallijst whose
 * `x_studio_projecttaak` points at a CHILD task must still surface under its
 * parent fase, so we MERGE every group whose `normalizeFaseId(group.taskId)`
 * equals the active fase id into a single active-fase group. When no pickings
 * exist for the fase the group is still rendered (as an empty upload target).
 */
import { useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { ProjectTask } from '@/types/odoo';
import { useProjectViewStore } from '@/stores/projectView';
import { usePermissionsStore } from '@/stores/permissions';
import {
  buildFaseGroups,
  buildParentByChild,
  normalizeFaseId,
  type FaseGroup,
} from '@/utils/materiaal';
import { MateriaalFaseGroup } from '@/components/project/materiaal/MateriaalFaseGroup';
import { MateriaalCadModals } from '@/components/project/materiaal/MateriaalCadModals';
import { useMateriaalFaseControls } from '@/hooks/useMateriaalFaseControls';

interface DashboardMateriaalSectionProps {
  projectId: number;
  projectTasks: ProjectTask[];
  /** The fase currently selected on the dashboard (always a number when rendered). */
  activeFaseId: number;
  /** True while the materiaal store slice is still loading for this project. */
  loading?: boolean;
  /** Materiaal-fetch error message, if any. */
  error?: string | null;
  /** Rendered inside a DashboardSection coloured shell — drop our own outer card
   *  chrome and the duplicated "Materiaal" title (the shell owns both). The
   *  materiaallijst-count caption + drag tip stay (genuine sub-context). */
  embedded?: boolean;
}

/**
 * Build the single FaseGroup for the active fase by merging every group whose
 * normalized fase id (parent-or-self) matches `activeFaseId`. Falls back to an
 * empty group (upload target) when there are no matching pickings.
 *
 * Exported so the Fase 6c "Alles" roll-up (DashboardMateriaalAlles) reuses the
 * EXACT same per-fase merge — a legacy materiaallijst on a CHILD task must
 * surface under its parent fase in both the per-fase view and the roll-up.
 */
export function buildActiveFaseGroup(
  allGroups: FaseGroup[],
  projectTasks: ProjectTask[],
  activeFaseId: number,
): FaseGroup {
  const parentByChild = buildParentByChild(projectTasks);
  const matching = allGroups.filter(
    (g) => normalizeFaseId(g.taskId, parentByChild) === activeFaseId,
  );

  // Prefer the group that IS the fase for the display name, then the fase task
  // itself, then the first matching (child-linked) group, then a generic label.
  const ownGroup = matching.find((g) => g.taskId === activeFaseId);
  const faseTask = projectTasks.find((t) => t.id === activeFaseId);
  const taskName =
    ownGroup?.taskName ?? faseTask?.name ?? matching[0]?.taskName ?? 'Onbekende fase';

  const pickingGroups = matching
    .flatMap((g) => g.pickingGroups)
    .sort((a, b) => a.picking.name.localeCompare(b.picking.name));

  return { taskId: activeFaseId, taskName, pickingGroups };
}

export function DashboardMateriaalSection({
  projectId,
  projectTasks,
  activeFaseId,
  loading = false,
  error = null,
  embedded = false,
}: DashboardMateriaalSectionProps) {
  const { materiaalLines, materiaalPickings, materiaalMoves, uploadError } = useProjectViewStore(
    useShallow((s) => ({
      materiaalLines: s.materiaalLines,
      materiaalPickings: s.materiaalPickings,
      materiaalMoves: s.materiaalMoves,
      uploadError: s.uploadError,
    })),
  );

  // `uploadError` is a shared store slice. Clear any stale message when entering
  // the section or switching fase/project, so an error from the Materiaal tab (or
  // another fase) doesn't bleed into this view.
  useEffect(() => {
    useProjectViewStore.setState({ uploadError: null });
  }, [projectId, activeFaseId]);

  // Gate writes on csv_upload. The dashboard now reaches non-admin roles (oplijster,
  // oplijster_easykit), so this gate is live: a role without csv_upload sees the
  // materiaal section read-only, matching its standalone MateriaalTab authority.
  const canUpload = usePermissionsStore((s) => s.canFeature)('csv_upload');

  const controls = useMateriaalFaseControls(projectId, projectTasks);

  const activeGroup = useMemo(() => {
    const allGroups = buildFaseGroups(
      materiaalPickings ?? [],
      materiaalLines ?? [],
      projectTasks,
      materiaalMoves ?? [],
    );
    return buildActiveFaseGroup(allGroups, projectTasks, activeFaseId);
  }, [materiaalPickings, materiaalLines, materiaalMoves, projectTasks, activeFaseId]);

  // Children of the active fase — drives the per-child opgelijst rows.
  const childTasks = useMemo(
    () =>
      projectTasks
        .filter((t) => t.parent_id && t.parent_id[0] === activeFaseId)
        .sort((a, b) => {
          if (!a.date_deadline && !b.date_deadline) return 0;
          if (!a.date_deadline) return 1;
          if (!b.date_deadline) return -1;
          return a.date_deadline.localeCompare(b.date_deadline);
        }),
    [projectTasks, activeFaseId],
  );

  const pickingCount = activeGroup.pickingGroups.length;

  // The store slices are null until the first fetch resolves; treat that as
  // loading so we don't flash an empty fase before pickings arrive.
  const stillLoading = loading || materiaalPickings === null;

  const Wrapper = embedded ? 'div' : 'section';
  return (
    <Wrapper
      className={embedded ? '' : 'rounded-xl border border-gray-200 bg-white p-4 sm:p-5'}
      aria-label="Materiaal"
    >
      <div className="mb-4">
        {!embedded && <h2 className="text-base font-extrabold text-td-navy">Materiaal</h2>}
        <p className="text-xs text-td-muted mt-0.5">
          {pickingCount} {pickingCount === 1 ? 'materiaallijst' : 'materiaallijsten'} in deze fase
        </p>
        {canUpload && (
          <p className="hidden sm:block text-xs text-td-muted mt-1">
            Tip: sleep een CSV-bestand op de fase om te uploaden.
          </p>
        )}
      </div>

      {/* Upload / delete / invalid-drop failures land in the shared uploadError
          slice — surface them here (parity with MateriaalTab), else they're silent. */}
      {uploadError && (
        <div className="mb-3 bg-red-50 text-red-600 p-3 rounded-xl flex items-start gap-3" role="alert">
          <div className="flex-1">
            <p className="font-semibold text-sm">Er ging iets mis</p>
            <p className="text-sm mt-0.5">{uploadError}</p>
          </div>
          <button
            type="button"
            onClick={() => useProjectViewStore.setState({ uploadError: null })}
            className="shrink-0 text-red-400 hover:text-red-600 transition-colors"
            aria-label="Foutmelding sluiten"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {error ? (
        <p className="text-sm text-td-magenta py-6 text-center">Materiaal laden mislukt: {error}</p>
      ) : stillLoading ? (
        <p className="text-sm text-td-muted py-6 text-center">Materiaal laden…</p>
      ) : (
        <div className="space-y-2">
          <MateriaalFaseGroup
            fase={activeGroup}
            defaultOpen
            isUploading={controls.isUploading}
            isEasykit={false}
            childTasks={childTasks}
            getOpgelijstValue={controls.getOpgelijstValue}
            onToggleOpgelijst={controls.onToggleOpgelijst}
            onUpload={async (file) => {
              await controls.onUpload(activeGroup.taskId, file);
            }}
            onCadClick={() => {
              controls.cad.openCad({ taskId: activeGroup.taskId, taskName: activeGroup.taskName });
            }}
            onDelete={controls.onDelete}
            onError={controls.onError}
            allowUpload={canUpload}
          />
        </div>
      )}

      <MateriaalCadModals controls={controls} projectId={projectId} />
    </Wrapper>
  );
}
